import { setSafeInnerHTML, escapeHtml } from './htmlSecurityModule.js';

const DEFAULT_FEATURES = Object.freeze({
  canAccessAi: false,
  canAccessMusic: false,
  canImportMusic: false,
  canManageAiModels: false,
  maxBooks: 20
});

const DEFAULT_MEMBERSHIP = Object.freeze({
  planCode: 'free',
  planName: 'Free',
  status: 'active',
  features: DEFAULT_FEATURES
});

const PLAN_ORDER = Object.freeze({
  free: 0,
  pro: 1,
  max: 2
});

const ROLE_LEVEL = Object.freeze({
  user: 0,
  support: 1,
  operator: 2,
  admin: 3,
  super_admin: 4
});

const FEATURE_META = Object.freeze([
  { key: 'canAccessAi', label: 'AI 助手' },
  { key: 'canAccessMusic', label: '音乐页' },
  { key: 'canImportMusic', label: '音乐导入' },
  { key: 'canManageAiModels', label: '模型管理' },
  { key: 'maxBooks', label: '书籍上限' }
]);

const CENTER_TABS = Object.freeze([
  { key: 'profile', label: '会员信息' },
  { key: 'plans', label: '权益档位' },
  { key: 'billing', label: '订阅与账单' },
  { key: 'security', label: '设备与安全' },
  { key: 'tickets', label: '客服工单' }
]);

let state = null;
let elements = null;
let showToast = () => {};
let switchView = () => {};

let membershipModuleInitialized = false;
let authMode = 'login';
let userMenuPortalReady = false;

let membershipCenterTab = 'profile';
let membershipCenterLoading = false;
let membershipCenterData = null;
const AVATAR_CROP_CANVAS_SIZE = 220;
const AVATAR_OUTPUT_SIZE = 256;
const AVATAR_MIN_ZOOM = 1;
const AVATAR_MAX_ZOOM = 3;

let profileEditorUserId = 0;
let profileAvatarSavedUrl = '';
let profileAvatarLocalOriginal = '';
let profileAvatarImage = null;
let profileAvatarBaseScale = 1;
let profileAvatarZoom = 1;
let profileAvatarOffsetX = 0;
let profileAvatarOffsetY = 0;
let profileAvatarCroppedDataUrl = '';

function bindDeps(context = {}) {
  if (context.state) state = context.state;
  if (context.elements) elements = context.elements;
  if (typeof context.showToast === 'function') showToast = context.showToast;
  if (typeof context.switchView === 'function') switchView = context.switchView;
}

function ensureAuthStateShape() {
  if (!state.auth || typeof state.auth !== 'object') {
    state.auth = {
      initialized: false,
      authenticated: false,
      user: null,
      membership: { ...DEFAULT_MEMBERSHIP },
      plans: []
    };
  }
  if (!state.auth.membership) {
    state.auth.membership = { ...DEFAULT_MEMBERSHIP };
  }
  if (!state.auth.membership.features || typeof state.auth.membership.features !== 'object') {
    state.auth.membership.features = { ...DEFAULT_FEATURES };
  }
  state.auth.membership.features = {
    ...DEFAULT_FEATURES,
    ...state.auth.membership.features
  };
}

function getAuthHeaders() {
  return { 'Content-Type': 'application/json' };
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    ...options
  });
  let payload = {};
  try {
    payload = await response.json();
  } catch {
  }
  if (!response.ok) {
    const err = new Error(payload?.error || '请求失败');
    err.status = response.status;
    throw err;
  }
  return payload;
}

async function requestBlob(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'include',
    ...options
  });
  if (!response.ok) {
    let payload = {};
    try {
      payload = await response.json();
    } catch {
    }
    const err = new Error(payload?.error || '下载失败');
    err.status = response.status;
    throw err;
  }
  return {
    blob: await response.blob(),
    disposition: String(response.headers.get('Content-Disposition') || '')
  };
}

function downloadBlob(blob, disposition, fallback = 'download.bin') {
  if (typeof document === 'undefined') return;
  const matched = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  const filename = matched?.[1] || fallback;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function normalizePlans(plans) {
  if (!Array.isArray(plans)) return [];
  return plans.map((plan) => ({
    code: String(plan?.code || 'free').toLowerCase(),
    name: String(plan?.name || 'Free'),
    priceCents: Number(plan?.priceCents || 0),
    periodDays: Number(plan?.periodDays || 30),
    features: {
      ...DEFAULT_FEATURES,
      ...(plan?.features || {})
    }
  }));
}

function getPlanRank(code) {
  return PLAN_ORDER[String(code || 'free').toLowerCase()] ?? 0;
}

function formatFeatureValue(featureKey, features = {}) {
  const value = features?.[featureKey];
  if (featureKey === 'maxBooks') {
    return Number.isFinite(value) ? `${value} 本` : '不限';
  }
  return value ? '可用' : '不可用';
}

function getPlanPriceLabel(plan) {
  return plan.priceCents > 0 ? `¥${(plan.priceCents / 100).toFixed(2)}/月` : '免费';
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', {
    hour12: false,
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('zh-CN');
}

function formatMoney(cents) {
  return `¥${(Number(cents || 0) / 100).toFixed(2)}`;
}

function clampNumber(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function initializeProfileEditorState() {
  const user = state?.auth?.user || {};
  const userId = Number(user?.id || 0);
  const nextSavedUrl = String(user?.avatarUrl || '').trim();
  if (profileEditorUserId !== userId) {
    profileEditorUserId = userId;
    profileAvatarSavedUrl = nextSavedUrl;
    profileAvatarLocalOriginal = '';
    profileAvatarImage = null;
    profileAvatarBaseScale = 1;
    profileAvatarZoom = 1;
    profileAvatarOffsetX = 0;
    profileAvatarOffsetY = 0;
    profileAvatarCroppedDataUrl = '';
    return;
  }
  if (!profileAvatarLocalOriginal && !profileAvatarCroppedDataUrl) {
    profileAvatarSavedUrl = nextSavedUrl;
  }
}

function getProfileAvatarPreviewUrl() {
  return profileAvatarCroppedDataUrl || profileAvatarSavedUrl || '';
}

function getAvatarDrawMetrics() {
  if (!profileAvatarImage) return null;
  const canvasSize = AVATAR_CROP_CANVAS_SIZE;
  const drawScale = profileAvatarBaseScale * profileAvatarZoom;
  const drawWidth = profileAvatarImage.naturalWidth * drawScale;
  const drawHeight = profileAvatarImage.naturalHeight * drawScale;
  const maxOffsetX = Math.max(0, (drawWidth - canvasSize) / 2);
  const maxOffsetY = Math.max(0, (drawHeight - canvasSize) / 2);
  return { canvasSize, drawScale, drawWidth, drawHeight, maxOffsetX, maxOffsetY };
}

function clampAvatarOffsets() {
  const metrics = getAvatarDrawMetrics();
  if (!metrics) return;
  profileAvatarOffsetX = clampNumber(profileAvatarOffsetX, -metrics.maxOffsetX, metrics.maxOffsetX);
  profileAvatarOffsetY = clampNumber(profileAvatarOffsetY, -metrics.maxOffsetY, metrics.maxOffsetY);
}

function drawAvatarCropCanvas(canvas) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  canvas.width = AVATAR_CROP_CANVAS_SIZE;
  canvas.height = AVATAR_CROP_CANVAS_SIZE;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (!profileAvatarImage) {
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'rgba(255,255,255,0.24)';
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
    ctx.setLineDash([]);
    return;
  }

  const metrics = getAvatarDrawMetrics();
  if (!metrics) return;

  ctx.fillStyle = '#0c1018';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const drawX = (canvas.width - metrics.drawWidth) / 2 + profileAvatarOffsetX;
  const drawY = (canvas.height - metrics.drawHeight) / 2 + profileAvatarOffsetY;
  ctx.drawImage(profileAvatarImage, drawX, drawY, metrics.drawWidth, metrics.drawHeight);

  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, canvas.width - 1, canvas.height - 1);
}

function createCroppedAvatarDataUrl() {
  if (!profileAvatarImage || typeof document === 'undefined') return '';
  const metrics = getAvatarDrawMetrics();
  if (!metrics) return '';

  const output = document.createElement('canvas');
  output.width = AVATAR_OUTPUT_SIZE;
  output.height = AVATAR_OUTPUT_SIZE;
  const outCtx = output.getContext('2d');
  if (!outCtx) return '';

  outCtx.fillStyle = '#0c1018';
  outCtx.fillRect(0, 0, output.width, output.height);

  const ratio = AVATAR_OUTPUT_SIZE / AVATAR_CROP_CANVAS_SIZE;
  const drawX = ((AVATAR_CROP_CANVAS_SIZE - metrics.drawWidth) / 2 + profileAvatarOffsetX) * ratio;
  const drawY = ((AVATAR_CROP_CANVAS_SIZE - metrics.drawHeight) / 2 + profileAvatarOffsetY) * ratio;
  const drawWidth = metrics.drawWidth * ratio;
  const drawHeight = metrics.drawHeight * ratio;
  outCtx.drawImage(profileAvatarImage, drawX, drawY, drawWidth, drawHeight);

  return output.toDataURL('image/jpeg', 0.86);
}

function updateAvatarPreviewDom() {
  const preview = elements.membershipPlans?.querySelector('#membershipProfileAvatarPreview');
  const fallback = elements.membershipPlans?.querySelector('#membershipProfileAvatarFallback');
  if (!preview || !fallback) return;
  const nextSrc = getProfileAvatarPreviewUrl();
  if (nextSrc) {
    preview.src = nextSrc;
    preview.hidden = false;
    fallback.style.display = 'none';
  } else {
    preview.removeAttribute('src');
    preview.hidden = true;
    fallback.style.display = '';
  }
}

function updateAvatarCropEditor(commitPreview = true) {
  clampAvatarOffsets();
  const canvas = elements.membershipPlans?.querySelector('#membershipAvatarCropCanvas');
  drawAvatarCropCanvas(canvas);
  if (commitPreview) {
    profileAvatarCroppedDataUrl = createCroppedAvatarDataUrl();
    updateAvatarPreviewDom();
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.readAsDataURL(file);
  });
}

function loadImageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = dataUrl;
  });
}

async function prepareLocalAvatar(file) {
  if (!file) return;
  const fileType = String(file.type || '').toLowerCase();
  if (!fileType.startsWith('image/')) {
    throw new Error('请选择图片文件');
  }
  if (Number(file.size || 0) > 8 * 1024 * 1024) {
    throw new Error('图片过大，请选择 8MB 以内文件');
  }
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImageFromDataUrl(dataUrl);
  profileAvatarLocalOriginal = dataUrl;
  profileAvatarImage = img;
  profileAvatarBaseScale = Math.max(
    AVATAR_CROP_CANVAS_SIZE / Math.max(1, img.naturalWidth),
    AVATAR_CROP_CANVAS_SIZE / Math.max(1, img.naturalHeight)
  );
  profileAvatarZoom = 1;
  profileAvatarOffsetX = 0;
  profileAvatarOffsetY = 0;
  profileAvatarCroppedDataUrl = '';
  updateAvatarCropEditor(true);
}

function resetAvatarEditorToSaved() {
  profileAvatarLocalOriginal = '';
  profileAvatarImage = null;
  profileAvatarBaseScale = 1;
  profileAvatarZoom = 1;
  profileAvatarOffsetX = 0;
  profileAvatarOffsetY = 0;
  profileAvatarCroppedDataUrl = '';
}

function applyMePayload(payload) {
  ensureAuthStateShape();
  state.auth.authenticated = !!payload?.authenticated;
  const rawUser = payload?.user || null;
  state.auth.user = rawUser
    ? {
      ...rawUser,
      role: String(rawUser?.role || 'user').toLowerCase()
    }
    : null;
  const membership = payload?.membership || DEFAULT_MEMBERSHIP;
  state.auth.membership = {
    planCode: String(membership?.planCode || 'free').toLowerCase(),
    planName: String(membership?.planName || 'Free'),
    status: String(membership?.status || 'active').toLowerCase(),
    features: {
      ...DEFAULT_FEATURES,
      ...(membership?.features || {})
    }
  };
  state.auth.initialized = true;
}

function emitMembershipChanged() {
  if (typeof window === 'undefined') return;
  if (typeof window.dispatchEvent !== 'function') return;
  if (typeof window.CustomEvent !== 'function') return;
  const detail = {
    authenticated: !!state?.auth?.authenticated,
    planCode: getMembershipPlanCode(state),
    features: getMembershipFeatures(state),
    role: String(state?.auth?.user?.role || ''),
    isAdmin: isCurrentUserAdmin(state)
  };
  window.dispatchEvent(new window.CustomEvent('membership:changed', { detail }));
}

function updateAvatarUi() {
  const user = state.auth?.user || null;
  const planCode = getMembershipPlanCode(state);
  const planLabel = String(state.auth?.membership?.planName || planCode || 'Free');

  if (elements.userMenuName) {
    elements.userMenuName.textContent = user?.nickname || user?.email || '访客';
  }
  if (elements.userMenuEmail) {
    elements.userMenuEmail.textContent = user?.email || '未登录';
  }
  if (elements.userPlanBadge) {
    elements.userPlanBadge.textContent = planLabel;
    elements.userPlanBadge.classList.remove('plan-free', 'plan-pro', 'plan-max');
    elements.userPlanBadge.classList.add(`plan-${planCode}`);
  }

  const fallback = (user?.nickname || user?.email || 'U').trim().charAt(0).toUpperCase() || 'U';
  if (elements.userAvatarFallback) {
    elements.userAvatarFallback.textContent = fallback;
  }
  if (elements.userAvatarImage) {
    if (user?.avatarUrl) {
      elements.userAvatarImage.src = user.avatarUrl;
      elements.userAvatarImage.hidden = false;
      if (elements.userAvatarFallback) elements.userAvatarFallback.style.display = 'none';
    } else {
      elements.userAvatarImage.removeAttribute('src');
      elements.userAvatarImage.hidden = true;
      if (elements.userAvatarFallback) elements.userAvatarFallback.style.display = '';
    }
  }

  if (elements.userLoginBtn) {
    elements.userLoginBtn.style.display = state.auth.authenticated ? 'none' : '';
  }
  if (elements.userLogoutBtn) {
    elements.userLogoutBtn.style.display = state.auth.authenticated ? '' : 'none';
  }

  if (elements.adminNavItem) {
    const isAdmin = isCurrentUserAdmin(state);
    elements.adminNavItem.style.display = isAdmin ? '' : 'none';
    elements.adminNavItem.classList.toggle('visible', isAdmin);
    elements.adminNavItem.setAttribute('aria-hidden', String(!isAdmin));
  }
}

function updateFeatureUi() {
  const canAi = canAccessFeature(state, 'canAccessAi');
  if (elements.aiFloatBtn) {
    elements.aiFloatBtn.style.display = canAi ? '' : 'none';
  }

  const canMusic = canAccessFeature(state, 'canAccessMusic');
  const musicNav = Array.from(elements.navItems || []).find((item) => item?.dataset?.view === 'music');
  if (musicNav) {
    musicNav.style.opacity = canMusic ? '1' : '0.4';
  }

  if (state.currentView === 'music' && !canMusic) {
    switchView('shelf');
    showToast('当前档位不可使用音乐页，请升级会员');
  }

  if (state.currentView === 'admin' && !isCurrentUserAdmin(state)) {
    switchView('shelf');
    showToast('仅支持/运营/管理员可访问管理台');
  }

  emitMembershipChanged();
}

function ensureUserMenuPortal() {
  const menu = elements.userMenu;
  if (!menu || userMenuPortalReady) return;
  if (typeof document === 'undefined' || !document.body) return;
  menu.classList.add('user-menu-portal');
  document.body.appendChild(menu);
  userMenuPortalReady = true;
}

function isUserMenuOpen() {
  return !!elements.userMenu?.classList.contains('open');
}

function positionUserMenu() {
  const menu = elements.userMenu;
  const trigger = elements.userAvatarBtn;
  if (!menu || !trigger || typeof window === 'undefined') return;

  const gap = 8;
  const triggerRect = trigger.getBoundingClientRect();
  const menuWidth = Math.round(menu.offsetWidth || 220);
  const menuHeight = Math.round(menu.offsetHeight || 170);

  let left = triggerRect.left + (triggerRect.width / 2) - (menuWidth / 2);
  left = Math.max(gap, Math.min(left, window.innerWidth - menuWidth - gap));

  let top = triggerRect.bottom + gap;
  if (top + menuHeight > window.innerHeight - gap) {
    top = Math.max(gap, triggerRect.top - menuHeight - gap);
  }

  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
}

function closeUserMenu() {
  elements.userAvatarWrap?.classList.remove('open');
  elements.userAvatarBtn?.setAttribute('aria-expanded', 'false');
  elements.userMenu?.classList.remove('open');
  elements.userMenu?.setAttribute('aria-hidden', 'true');
}

function toggleUserMenu() {
  ensureUserMenuPortal();
  const root = elements.userAvatarWrap;
  const menu = elements.userMenu;
  if (!root || !menu) return;
  const next = !menu.classList.contains('open');
  root.classList.toggle('open', next);
  elements.userAvatarBtn?.setAttribute('aria-expanded', String(next));
  menu.classList.toggle('open', next);
  menu.setAttribute('aria-hidden', String(!next));
  if (next) {
    positionUserMenu();
  }
}

function openAuthOverlay(mode = 'login') {
  authMode = mode === 'register' ? 'register' : 'login';
  if (elements.userAuthTitle) {
    elements.userAuthTitle.textContent = authMode === 'register' ? '注册' : '登录';
  }
  if (elements.userAuthSubmitBtn) {
    elements.userAuthSubmitBtn.textContent = authMode === 'register' ? '注册并登录' : '登录';
  }
  if (elements.userAuthToggleModeBtn) {
    elements.userAuthToggleModeBtn.textContent = authMode === 'register' ? '已有账号？去登录' : '没有账号？去注册';
  }
  if (elements.userAuthNicknameWrap) {
    elements.userAuthNicknameWrap.style.display = authMode === 'register' ? '' : 'none';
  }
  elements.userAuthOverlay?.classList.add('active');
  elements.userAuthOverlay?.setAttribute('aria-hidden', 'false');
  setTimeout(() => elements.userAuthEmail?.focus(), 0);
}

function closeAuthOverlay() {
  elements.userAuthOverlay?.classList.remove('active');
  elements.userAuthOverlay?.setAttribute('aria-hidden', 'true');
}

function getActiveCenterTab() {
  return CENTER_TABS.some((tab) => tab.key === membershipCenterTab) ? membershipCenterTab : 'profile';
}

function setCenterTab(tab) {
  const normalized = String(tab || '').toLowerCase();
  if (!CENTER_TABS.some((item) => item.key === normalized)) return;
  membershipCenterTab = normalized;
  renderMembershipCenter();
}

function describePlanFeatures(features = {}) {
  return FEATURE_META.map((meta) => ({
    label: meta.label,
    value: formatFeatureValue(meta.key, features),
    available: meta.key === 'maxBooks' ? true : !!features?.[meta.key]
  }));
}

function renderPlanCompareAndCards() {
  const plans = normalizePlans(state.auth?.plans);
  if (!plans.length) {
    return '<p class="settings-hint">暂无可用档位</p>';
  }

  const currentPlan = getMembershipPlanCode(state);
  const compareHead = plans.map((plan) => {
    const isCurrent = plan.code === currentPlan;
    return `
      <th class="${isCurrent ? 'is-current' : ''}">
        <div class="membership-compare-plan-name">${escapeHtml(plan.name)}</div>
        <div class="membership-compare-plan-price">${escapeHtml(getPlanPriceLabel(plan))}</div>
      </th>
    `;
  }).join('');

  const compareRows = FEATURE_META.map((meta) => {
    const cells = plans.map((plan) => {
      const value = formatFeatureValue(meta.key, plan.features);
      const binary = meta.key === 'maxBooks' ? null : !!plan.features?.[meta.key];
      const cls = binary === null ? '' : (binary ? 'is-on' : 'is-off');
      return `<td class="${cls}">${escapeHtml(value)}</td>`;
    }).join('');
    return `
      <tr>
        <th>${escapeHtml(meta.label)}</th>
        ${cells}
      </tr>
    `;
  }).join('');

  const cards = plans.map((plan) => {
    const featureList = describePlanFeatures(plan.features)
      .map((item) => `<div class="membership-plan-feature ${item.available ? 'is-on' : 'is-off'}">${escapeHtml(item.label)}：${escapeHtml(item.value)}</div>`)
      .join('');
    const isCurrent = plan.code === currentPlan;
    const currentRank = getPlanRank(currentPlan);
    const nextRank = getPlanRank(plan.code);
    let actionLabel = '切换到此档位';
    if (nextRank > currentRank) actionLabel = '升级到此档位';
    if (nextRank < currentRank) actionLabel = '降级到此档位';

    return `
      <div class="membership-plan-item ${isCurrent ? 'is-current' : ''}" data-plan="${escapeHtml(plan.code)}">
        <div class="membership-plan-head">
          <span class="membership-plan-name">
            ${escapeHtml(plan.name)}
            ${isCurrent ? '<span class="membership-plan-tag">当前</span>' : ''}
          </span>
          <span class="membership-plan-price">${escapeHtml(getPlanPriceLabel(plan))}</span>
        </div>
        <div class="membership-plan-features">${featureList}</div>
        <button class="membership-plan-action" data-plan-action="${escapeHtml(plan.code)}" ${isCurrent ? 'disabled' : ''}>
          ${isCurrent ? '当前档位' : actionLabel}
        </button>
      </div>
    `;
  }).join('');

  return `
    <div class="membership-compare">
      <table>
        <thead>
          <tr>
            <th>权益</th>
            ${compareHead}
          </tr>
        </thead>
        <tbody>
          ${compareRows}
        </tbody>
      </table>
    </div>
    ${cards}
  `;
}

function renderMembershipCenter() {
  if (!elements.membershipPlans) return;

  if (elements.membershipSummary) {
    const planName = String(state.auth?.membership?.planName || 'Free');
    const status = String(state.auth?.membership?.status || 'active').toLowerCase();
    elements.membershipSummary.textContent = `当前档位：${planName} · 状态：${status}`;
  }

  if (membershipCenterLoading) {
    setSafeInnerHTML(elements.membershipPlans, '<div class="membership-center-loading">会员中心加载中...</div>', { profile: 'app' });
    return;
  }

  if (!membershipCenterData) {
    setSafeInnerHTML(elements.membershipPlans, '<div class="membership-center-loading">暂无会员数据</div>', { profile: 'app' });
    return;
  }

  const center = membershipCenterData;
  const activeTab = getActiveCenterTab();

  const tabsHtml = CENTER_TABS.map((tab) => `
    <button class="membership-center-tab ${tab.key === activeTab ? 'active' : ''}" type="button" data-center-tab="${tab.key}">
      ${escapeHtml(tab.label)}
    </button>
  `).join('');

  const profileSection = (() => {
    initializeProfileEditorState();
    const user = state?.auth?.user || {};
    const profile = center?.profile || {};
    const email = String(user.email || profile.email || '-');
    const registeredAt = formatDateTime(profile.registeredAt);
    const journeyDays = Number(profile.journeyDays || 0);
    const avatarPreview = getProfileAvatarPreviewUrl();
    const fallback = (user?.nickname || user?.email || 'U').trim().charAt(0).toUpperCase() || 'U';
    return `
      <section class="membership-center-section ${activeTab === 'profile' ? 'active' : ''}" data-center-section="profile">
        <div class="membership-grid-two">
          <article class="membership-card-lite">
            <h4>账户信息</h4>
            <div class="membership-muted">邮箱：${escapeHtml(email)}</div>
            <div class="membership-muted">注册时间：${escapeHtml(registeredAt)}</div>
            <div class="membership-muted">旅程天数：${escapeHtml(journeyDays > 0 ? `${journeyDays} 天` : '-')}</div>
            <div class="membership-muted">当前档位：${escapeHtml(String(profile.planName || profile.planCode || state?.auth?.membership?.planName || '-'))}</div>
            <div class="membership-muted">会员状态：${escapeHtml(String(profile.status || state?.auth?.membership?.status || '-'))}</div>
            <div class="membership-muted">到期时间：${escapeHtml(formatDateTime(profile.expiresAt))}</div>
          </article>

          <article class="membership-card-lite">
            <h4>会员信息设置</h4>
            <input class="membership-input" id="membershipProfileNickname" type="text" placeholder="昵称" maxlength="64" value="${escapeHtml(String(user.nickname || ''))}">
            <div class="membership-avatar-editor">
              <div class="membership-avatar-preview-wrap">
                <img class="membership-avatar-preview" id="membershipProfileAvatarPreview" alt="头像预览" ${avatarPreview ? `src="${escapeHtml(avatarPreview)}"` : ''} ${avatarPreview ? '' : 'hidden'}>
                <span class="membership-avatar-preview-fallback" id="membershipProfileAvatarFallback" ${avatarPreview ? 'style="display:none;"' : ''}>${escapeHtml(fallback)}</span>
              </div>
              <div class="membership-inline-actions membership-avatar-actions">
                <button class="membership-inline-btn" type="button" data-center-action="avatar-pick">本地上传</button>
                <button class="membership-inline-btn" type="button" data-center-action="avatar-reset">恢复已保存</button>
                <button class="membership-inline-btn" type="button" data-center-action="avatar-clear">清空头像</button>
              </div>
              <input id="membershipProfileAvatarUpload" type="file" accept="image/*" style="display:none;" />
              <div class="membership-avatar-crop-shell">
                <canvas id="membershipAvatarCropCanvas" class="membership-avatar-crop-canvas" width="${AVATAR_CROP_CANVAS_SIZE}" height="${AVATAR_CROP_CANVAS_SIZE}" aria-label="头像裁剪画布"></canvas>
                <div class="membership-avatar-crop-toolbar">
                  <label class="membership-muted" for="membershipAvatarZoom">缩放</label>
                  <input class="membership-avatar-zoom" id="membershipAvatarZoom" type="range" min="${AVATAR_MIN_ZOOM}" max="${AVATAR_MAX_ZOOM}" step="0.01" value="${escapeHtml(profileAvatarImage ? String(profileAvatarZoom) : '1')}">
                </div>
                <div class="membership-muted">原图仅在本地缓存，拖动画布调位置，保存时仅上传裁剪结果。</div>
              </div>
            </div>
            <button class="membership-primary-btn" type="button" data-center-action="save-profile">保存资料</button>
          </article>
        </div>
      </section>
    `;
  })();

  const planSection = (() => {
    const recommendation = center?.recommendation || {};
    return `
      <section class="membership-center-section ${activeTab === 'plans' ? 'active' : ''}" data-center-section="plans">
        <div class="membership-center-tip">${escapeHtml(String(recommendation.reason || ''))}</div>
        ${renderPlanCompareAndCards()}
      </section>
    `;
  })();

  const billingSection = (() => {
    const profile = center?.billing?.profile || {};
    const annualOffer = center?.billing?.annualOffer || {};
    const invoices = Array.isArray(center?.billing?.invoices) ? center.billing.invoices : [];
    const invoiceRows = invoices.map((item) => `
      <tr>
        <td>${escapeHtml(String(item.invoiceNo || '-'))}</td>
        <td>${escapeHtml(formatMoney(item.amountCents))}</td>
        <td>${escapeHtml(String(item.status || '-'))}</td>
        <td>${escapeHtml(formatDate(item.issuedAt))}</td>
        <td>
          <button class="membership-inline-btn" type="button" data-center-action="invoice-download" data-invoice-id="${Number(item.id || 0)}">PDF</button>
          ${String(item.status || '').toLowerCase() === 'failed' || String(item.status || '').toLowerCase() === 'pending'
            ? `<button class="membership-inline-btn" type="button" data-center-action="payment-retry" data-invoice-id="${Number(item.id || 0)}">重试支付</button>`
            : ''}
        </td>
      </tr>
    `).join('');

    return `
      <section class="membership-center-section ${activeTab === 'billing' ? 'active' : ''}" data-center-section="billing">
        <div class="membership-grid-two">
          <article class="membership-card-lite">
            <h4>发票信息</h4>
            <input class="membership-input" id="membershipBillingTitle" type="text" placeholder="发票抬头" value="${escapeHtml(String(profile.invoiceTitle || ''))}">
            <input class="membership-input" id="membershipBillingTax" type="text" placeholder="税号" value="${escapeHtml(String(profile.taxNumber || ''))}">
            <input class="membership-input" id="membershipBillingEmail" type="email" placeholder="账单邮箱" value="${escapeHtml(String(profile.billingEmail || ''))}">
            <button class="membership-primary-btn" type="button" data-center-action="save-billing-profile">保存发票信息</button>
          </article>

          <article class="membership-card-lite">
            <h4>年付优惠</h4>
            <div class="membership-big-text">${escapeHtml(String(annualOffer.discountPercent || 0))}% OFF</div>
            <div class="membership-muted">年付价格：${escapeHtml(formatMoney(annualOffer.annualPriceCents || 0))}</div>
            <div class="membership-muted">支持升级补差价与到期降级生效。</div>
          </article>
        </div>

        <div class="membership-table-wrap">
          <table class="membership-table">
            <thead>
              <tr><th>发票号</th><th>金额</th><th>状态</th><th>开票日</th><th>操作</th></tr>
            </thead>
            <tbody>${invoiceRows || '<tr><td colspan="5">暂无账单记录</td></tr>'}</tbody>
          </table>
        </div>
      </section>
    `;
  })();

  const lifecycleSection = (() => {
    const lifecycle = center?.lifecycle || {};
    const paused = !!lifecycle.paused;
    return `
      <section class="membership-center-section ${activeTab === 'billing' ? 'active' : ''}" data-center-section="lifecycle">
        <div class="membership-grid-two">
          <article class="membership-card-lite">
            <h4>生命周期状态</h4>
            <div class="membership-muted">下次扣费日：${escapeHtml(formatDate(lifecycle.nextBillingAt))}</div>
            <div class="membership-muted">剩余天数：${escapeHtml(String(lifecycle.daysRemaining ?? '-'))}</div>
            <div class="membership-muted">试用剩余：${escapeHtml(String(lifecycle.trialRemainingDays ?? 0))} 天</div>
            <div class="membership-inline-actions">
              <button class="membership-inline-btn" type="button" data-center-action="toggle-pause" data-paused="${paused ? 'false' : 'true'}">${paused ? '恢复订阅' : '暂停订阅'}</button>
            </div>
          </article>

          <article class="membership-card-lite">
            <h4>提醒设置</h4>
            <label class="membership-check-row">
              <input id="membershipLifecycleAutoRenew" type="checkbox" ${center?.profile?.autoRenew ? 'checked' : ''}>
              <span>自动续费</span>
            </label>
            <label class="membership-check-row">
              <input id="membershipLifecycleEmail" type="checkbox" ${lifecycle.lifecycleEmailEnabled ? 'checked' : ''}>
              <span>邮件提醒</span>
            </label>
            <label class="membership-muted" for="membershipLifecycleReminderDays">提前提醒天数</label>
            <input class="membership-input" id="membershipLifecycleReminderDays" type="number" min="1" max="30" value="${Number(lifecycle.reminderDays || 3)}">
            <button class="membership-primary-btn" type="button" data-center-action="save-lifecycle">保存生命周期设置</button>
          </article>
        </div>
      </section>
    `;
  })();

  const usageSection = (() => {
    const usage = center?.usage || {};
    const gateLogs = Array.isArray(usage?.gateLogs) ? usage.gateLogs : [];
    const gateRows = gateLogs.map((item) => `
      <tr>
        <td>${escapeHtml(String(item.feature || '-'))}</td>
        <td>${escapeHtml(item.allowed ? '允许' : '拦截')}</td>
        <td>${escapeHtml(String(item.reason || '-'))}</td>
        <td>${escapeHtml(formatDateTime(item.createdAt))}</td>
      </tr>
    `).join('');

    return `
      <section class="membership-center-section ${activeTab === 'billing' ? 'active' : ''}" data-center-section="usage">
        <div class="membership-grid-four">
          <article class="membership-stat"><div>AI 次数</div><strong>${Number(usage.aiCount || 0)}</strong></article>
          <article class="membership-stat"><div>音乐次数</div><strong>${Number(usage.musicCount || 0)}</strong></article>
          <article class="membership-stat"><div>门禁拦截</div><strong>${Number(usage.gateDeniedCount || 0)}</strong></article>
          <article class="membership-stat"><div>并发占用</div><strong>${Number(usage?.limits?.concurrentDevices || 0)} / ${Number(usage?.limits?.concurrentLimit || 0)}</strong></article>
        </div>
        <div class="membership-muted">设备占用：${Number(usage?.limits?.activeDevices || 0)} / ${Number(usage?.limits?.deviceLimit || 0)}</div>

        <div class="membership-table-wrap">
          <table class="membership-table">
            <thead>
              <tr><th>功能</th><th>结果</th><th>原因</th><th>时间</th></tr>
            </thead>
            <tbody>${gateRows || '<tr><td colspan="4">暂无门禁记录</td></tr>'}</tbody>
          </table>
        </div>
      </section>
    `;
  })();

  const devicesSection = (() => {
    const devices = Array.isArray(center?.devices?.items) ? center.devices.items : [];
    const anomaly = center?.devices?.anomaly || {};
    const rows = devices.map((item) => `
      <tr>
        <td>${escapeHtml(String(item.label || item.deviceId || '-'))}</td>
        <td>${escapeHtml(item.online ? '在线' : '离线')}</td>
        <td>${escapeHtml(item.trusted ? '已信任' : '未信任')}</td>
        <td>${escapeHtml(formatDateTime(item.lastSeenAt))}</td>
        <td>
          <button class="membership-inline-btn" type="button" data-center-action="device-trust" data-device-id="${escapeHtml(String(item.deviceId || ''))}" data-device-trusted="${item.trusted ? 'false' : 'true'}">${item.trusted ? '取消信任' : '设为信任'}</button>
          <button class="membership-inline-btn" type="button" data-center-action="device-revoke" data-device-id="${escapeHtml(String(item.deviceId || ''))}">强制下线</button>
        </td>
      </tr>
    `).join('');

    return `
      <section class="membership-center-section ${activeTab === 'security' ? 'active' : ''}" data-center-section="devices">
        <div class="membership-alert ${anomaly.hasAnomaly ? 'is-risk' : ''}">
          近24小时失败登录：${Number(anomaly.failed24h || 0)}，7天 IP：${Number(anomaly.ipCount7d || 0)}，UA：${Number(anomaly.uaCount7d || 0)}
        </div>
        <div class="membership-table-wrap">
          <table class="membership-table">
            <thead>
              <tr><th>设备</th><th>状态</th><th>信任</th><th>最近活跃</th><th>操作</th></tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="5">暂无设备记录</td></tr>'}</tbody>
          </table>
        </div>
      </section>
    `;
  })();

  const securitySection = (() => {
    const security = center?.security || {};
    const apiKeys = Array.isArray(security?.apiKeys) ? security.apiKeys : [];
    const loginHistory = Array.isArray(security?.loginHistory) ? security.loginHistory : [];

    const apiKeyRows = apiKeys.map((item) => `
      <tr>
        <td>${escapeHtml(String(item.name || '-'))}</td>
        <td>${escapeHtml(String(item.keyPrefix || '-'))}</td>
        <td>${escapeHtml(Array.isArray(item.scopes) ? item.scopes.join(', ') : 'reader')}</td>
        <td>${escapeHtml(String(item.status || 'active'))}</td>
        <td><button class="membership-inline-btn" type="button" data-center-action="api-key-revoke" data-api-key-id="${Number(item.id || 0)}">吊销</button></td>
      </tr>
    `).join('');

    const loginRows = loginHistory.map((item) => `
      <tr>
        <td>${escapeHtml(item.success ? '成功' : '失败')}</td>
        <td>${escapeHtml(String(item.ip || '-'))}</td>
        <td>${escapeHtml(String(item.userAgent || '-').slice(0, 80))}</td>
        <td>${escapeHtml(formatDateTime(item.createdAt))}</td>
      </tr>
    `).join('');

    return `
      <section class="membership-center-section ${activeTab === 'security' ? 'active' : ''}" data-center-section="security">
        <div class="membership-grid-two">
          <article class="membership-card-lite">
            <h4>安全设置</h4>
            <label class="membership-check-row">
              <input id="membershipSecurity2FA" type="checkbox" ${security.twoFactorEnabled ? 'checked' : ''}>
              <span>开启两步验证（2FA）</span>
            </label>
            <label class="membership-check-row">
              <input id="membershipSecuritySensitiveConfirm" type="checkbox" ${security.requireSensitiveConfirm ? 'checked' : ''}>
              <span>敏感操作二次确认</span>
            </label>
            <button class="membership-primary-btn" type="button" data-center-action="save-security">保存安全设置</button>
          </article>

          <article class="membership-card-lite">
            <h4>创建 API Key</h4>
            <input class="membership-input" id="membershipApiKeyName" type="text" placeholder="密钥名称" maxlength="64">
            <input class="membership-input" id="membershipApiKeyScopes" type="text" placeholder="权限范围：reader,ai,music">
            <button class="membership-primary-btn" type="button" data-center-action="api-key-create">创建密钥</button>
          </article>
        </div>

        <div class="membership-table-wrap">
          <table class="membership-table">
            <thead>
              <tr><th>名称</th><th>前缀</th><th>Scopes</th><th>状态</th><th>操作</th></tr>
            </thead>
            <tbody>${apiKeyRows || '<tr><td colspan="5">暂无 API Key</td></tr>'}</tbody>
          </table>
        </div>

        <div class="membership-table-wrap">
          <table class="membership-table">
            <thead>
              <tr><th>结果</th><th>IP</th><th>UA</th><th>时间</th></tr>
            </thead>
            <tbody>${loginRows || '<tr><td colspan="4">暂无登录记录</td></tr>'}</tbody>
          </table>
        </div>
      </section>
    `;
  })();

  const ticketsSection = (() => {
    const tickets = Array.isArray(center?.support?.tickets) ? center.support.tickets : [];
    const rows = tickets.map((item) => {
      const status = String(item.status || 'open');
      const attachments = Array.isArray(item.attachments) ? item.attachments : [];
      return `
        <tr data-ticket-id="${Number(item.id || 0)}">
          <td>#${Number(item.id || 0)}</td>
          <td>${escapeHtml(String(item.note || '-'))}</td>
          <td>${attachments.length ? escapeHtml(attachments.join(', ')) : '-'}</td>
          <td>
            <select class="membership-select" data-ticket-status="1">
              <option value="open" ${status === 'open' ? 'selected' : ''}>Open</option>
              <option value="in_progress" ${status === 'in_progress' ? 'selected' : ''}>In Progress</option>
              <option value="resolved" ${status === 'resolved' ? 'selected' : ''}>Resolved</option>
            </select>
          </td>
          <td>${escapeHtml(formatDateTime(item.updatedAt))}</td>
          <td><button class="membership-inline-btn" type="button" data-center-action="ticket-status-save">更新</button></td>
        </tr>
      `;
    }).join('');

    return `
      <section class="membership-center-section ${activeTab === 'tickets' ? 'active' : ''}" data-center-section="tickets">
        <article class="membership-card-lite">
          <h4>提交工单</h4>
          <textarea class="membership-textarea" id="membershipTicketNote" placeholder="描述你的问题或诉求"></textarea>
          <input class="membership-input" id="membershipTicketAttachment" type="text" placeholder="附件地址（可选）">
          <button class="membership-primary-btn" type="button" data-center-action="ticket-create">提交工单</button>
        </article>
        <div class="membership-table-wrap">
          <table class="membership-table">
            <thead>
              <tr><th>ID</th><th>内容</th><th>附件</th><th>状态</th><th>更新时间</th><th>操作</th></tr>
            </thead>
            <tbody>${rows || '<tr><td colspan="6">暂无工单</td></tr>'}</tbody>
          </table>
        </div>
      </section>
    `;
  })();

  const html = `
    <div class="membership-center-head">
      <div class="membership-center-tabs">${tabsHtml}</div>
    </div>
    ${profileSection}
    ${planSection}
    ${billingSection}
    ${lifecycleSection}
    ${usageSection}
    ${devicesSection}
    ${securitySection}
    ${ticketsSection}
  `;

  setSafeInnerHTML(elements.membershipPlans, html, { profile: 'app' });
  bindProfileEditorDom(activeTab);
}

function bindProfileEditorDom(activeTab) {
  if (activeTab !== 'profile') return;
  const root = elements.membershipPlans;
  if (!root) return;

  const uploadInput = root.querySelector('#membershipProfileAvatarUpload');
  const zoomInput = root.querySelector('#membershipAvatarZoom');
  const cropCanvas = root.querySelector('#membershipAvatarCropCanvas');

  if (uploadInput) {
    uploadInput.addEventListener('change', async () => {
      const file = uploadInput.files?.[0];
      if (!file) return;
      try {
        await prepareLocalAvatar(file);
        if (zoomInput) zoomInput.value = String(profileAvatarZoom);
      } catch (error) {
        showToast(error?.message || '图片处理失败');
      } finally {
        uploadInput.value = '';
      }
    });
  }

  if (zoomInput) {
    zoomInput.value = String(profileAvatarImage ? profileAvatarZoom : 1);
    zoomInput.addEventListener('input', () => {
      profileAvatarZoom = clampNumber(Number(zoomInput.value), AVATAR_MIN_ZOOM, AVATAR_MAX_ZOOM);
      updateAvatarCropEditor(true);
    });
  }

  if (cropCanvas) {
    let dragState = null;

    cropCanvas.addEventListener('pointerdown', (event) => {
      if (!profileAvatarImage) return;
      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: profileAvatarOffsetX,
        originY: profileAvatarOffsetY
      };
      cropCanvas.setPointerCapture(event.pointerId);
    });

    cropCanvas.addEventListener('pointermove', (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      profileAvatarOffsetX = dragState.originX + (event.clientX - dragState.startX);
      profileAvatarOffsetY = dragState.originY + (event.clientY - dragState.startY);
      updateAvatarCropEditor(false);
    });

    const finishDrag = (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      dragState = null;
      updateAvatarCropEditor(true);
    };
    cropCanvas.addEventListener('pointerup', finishDrag);
    cropCanvas.addEventListener('pointercancel', finishDrag);
  }

  updateAvatarCropEditor(true);
}

async function refreshMe() {
  const me = await requestJson('/api/auth/me');
  applyMePayload(me);
  updateAvatarUi();
  updateFeatureUi();
}

async function loadMembershipCenterData(silent = false) {
  if (!state?.auth?.authenticated) return;
  if (!silent) {
    membershipCenterLoading = true;
    renderMembershipCenter();
  }
  try {
    const payload = await requestJson('/api/membership/center');
    membershipCenterData = payload?.center || null;
  } catch (error) {
    if (!silent) showToast(error?.message || '会员中心加载失败');
  } finally {
    membershipCenterLoading = false;
    renderMembershipCenter();
  }
}

export function openMembershipCenter() {
  if (!state?.auth?.authenticated) {
    openAuthOverlay('login');
    return;
  }
  renderMembershipCenter();
  elements.membershipOverlay?.classList.add('active');
  elements.membershipOverlay?.setAttribute('aria-hidden', 'false');
  loadMembershipCenterData();
}

function closeMembershipCenter() {
  elements.membershipOverlay?.classList.remove('active');
  elements.membershipOverlay?.setAttribute('aria-hidden', 'true');
}

async function submitAuth() {
  const email = String(elements.userAuthEmail?.value || '').trim();
  const password = String(elements.userAuthPassword?.value || '');
  const nickname = String(elements.userAuthNickname?.value || '').trim();

  if (!email || !password) {
    showToast('请输入邮箱和密码');
    return;
  }

  const endpoint = authMode === 'register' ? '/api/auth/register' : '/api/auth/login';
  const payload = authMode === 'register'
    ? { email, password, nickname }
    : { email, password };

  try {
    const me = await requestJson(endpoint, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    applyMePayload(me);
    closeAuthOverlay();
    closeUserMenu();
    updateAvatarUi();
    updateFeatureUi();
    showToast(authMode === 'register' ? '注册成功' : '登录成功');
  } catch (error) {
    showToast(error?.message || '操作失败');
  }
}

async function logout() {
  try {
    await requestJson('/api/auth/logout', { method: 'POST' });
  } catch {
  }
  ensureAuthStateShape();
  state.auth.authenticated = false;
  state.auth.user = null;
  state.auth.membership = {
    ...DEFAULT_MEMBERSHIP,
    features: { ...DEFAULT_FEATURES }
  };
  profileEditorUserId = 0;
  profileAvatarSavedUrl = '';
  resetAvatarEditorToSaved();
  membershipCenterData = null;
  closeUserMenu();
  updateAvatarUi();
  updateFeatureUi();
}

async function switchPlan(planCode) {
  if (!state.auth?.authenticated) {
    closeMembershipCenter();
    openAuthOverlay('login');
    return;
  }
  const plans = normalizePlans(state.auth?.plans);
  const target = plans.find((plan) => plan.code === String(planCode || '').toLowerCase());
  const currentCode = getMembershipPlanCode(state);
  const currentPlan = plans.find((plan) => plan.code === currentCode);
  if (!target || target.code === currentCode) return;

  const currentRank = getPlanRank(currentCode);
  const targetRank = getPlanRank(target.code);
  const action = targetRank > currentRank ? '升级' : '降级';
  const confirmText = `确认${action}到 ${target.name} 吗？\n当前：${currentPlan?.name || currentCode.toUpperCase()}\n目标：${target.name}`;
  if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
    if (!window.confirm(confirmText)) return;
  }

  try {
    const me = await requestJson('/api/membership/switch-plan', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ planCode })
    });
    applyMePayload(me);
    updateAvatarUi();
    updateFeatureUi();
    await loadMembershipCenterData(true);
    showToast(`${action}成功，当前为 ${target.name}`);
  } catch (error) {
    showToast(error?.message || '切换档位失败');
  }
}

async function performCenterAction(action, triggerEl = null) {
  try {
    if (action === 'refresh') {
      await loadMembershipCenterData();
      return;
    }

    if (action === 'avatar-pick') {
      const input = elements.membershipPlans?.querySelector('#membershipProfileAvatarUpload');
      input?.click();
      return;
    }

    if (action === 'avatar-reset') {
      resetAvatarEditorToSaved();
      updateAvatarCropEditor(true);
      showToast('已恢复为已保存头像');
      return;
    }

    if (action === 'avatar-clear') {
      profileAvatarSavedUrl = '';
      resetAvatarEditorToSaved();
      updateAvatarPreviewDom();
      updateAvatarCropEditor(true);
      showToast('头像已清空，点击“保存资料”后生效');
      return;
    }

    if (action === 'save-profile') {
      const nickname = String(elements.membershipPlans?.querySelector('#membershipProfileNickname')?.value || '').trim();
      const avatarUrl = profileAvatarCroppedDataUrl || profileAvatarSavedUrl || '';
      await requestJson('/api/profile', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ nickname, avatarUrl })
      });
      await refreshMe();
      profileAvatarSavedUrl = String(state?.auth?.user?.avatarUrl || '').trim();
      resetAvatarEditorToSaved();
      await loadMembershipCenterData(true);
      showToast('会员资料已更新');
      return;
    }

    if (action === 'save-billing-profile') {
      const invoiceTitle = String(elements.membershipPlans?.querySelector('#membershipBillingTitle')?.value || '').trim();
      const taxNumber = String(elements.membershipPlans?.querySelector('#membershipBillingTax')?.value || '').trim();
      const billingEmail = String(elements.membershipPlans?.querySelector('#membershipBillingEmail')?.value || '').trim();
      await requestJson('/api/membership/center/billing-profile', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invoiceTitle, taxNumber, billingEmail })
      });
      showToast('发票信息已保存');
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'invoice-download') {
      const invoiceId = Number(triggerEl?.getAttribute('data-invoice-id') || 0);
      if (!invoiceId) return;
      const { blob, disposition } = await requestBlob(`/api/membership/center/invoices/${invoiceId}/pdf`);
      downloadBlob(blob, disposition, `invoice-${invoiceId}.pdf`);
      return;
    }

    if (action === 'payment-retry') {
      const invoiceId = Number(triggerEl?.getAttribute('data-invoice-id') || 0) || undefined;
      await requestJson('/api/membership/center/payment/retry', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ invoiceId })
      });
      showToast('支付重试已完成');
      await refreshMe();
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'save-lifecycle') {
      const autoRenew = !!elements.membershipPlans?.querySelector('#membershipLifecycleAutoRenew')?.checked;
      const lifecycleEmailEnabled = !!elements.membershipPlans?.querySelector('#membershipLifecycleEmail')?.checked;
      const reminderDays = Number(elements.membershipPlans?.querySelector('#membershipLifecycleReminderDays')?.value || 3);
      await requestJson('/api/membership/center/lifecycle', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ autoRenew, lifecycleEmailEnabled, reminderDays })
      });
      showToast('生命周期设置已保存');
      await refreshMe();
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'toggle-pause') {
      const paused = String(triggerEl?.getAttribute('data-paused') || 'false') === 'true';
      await requestJson('/api/membership/center/lifecycle', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ paused })
      });
      showToast(paused ? '订阅已暂停' : '订阅已恢复');
      await refreshMe();
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'device-revoke') {
      const deviceId = String(triggerEl?.getAttribute('data-device-id') || '');
      if (!deviceId) return;
      await requestJson(`/api/membership/center/devices/${encodeURIComponent(deviceId)}/revoke`, { method: 'POST' });
      showToast('设备已下线');
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'device-trust') {
      const deviceId = String(triggerEl?.getAttribute('data-device-id') || '');
      const trusted = String(triggerEl?.getAttribute('data-device-trusted') || 'false') === 'true';
      if (!deviceId) return;
      await requestJson(`/api/membership/center/devices/${encodeURIComponent(deviceId)}/trust`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ trusted })
      });
      showToast(trusted ? '设备已设为信任' : '设备已取消信任');
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'save-security') {
      const twoFactorEnabled = !!elements.membershipPlans?.querySelector('#membershipSecurity2FA')?.checked;
      const requireSensitiveConfirm = !!elements.membershipPlans?.querySelector('#membershipSecuritySensitiveConfirm')?.checked;
      await requestJson('/api/membership/center/security', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ twoFactorEnabled, requireSensitiveConfirm })
      });
      showToast('安全设置已保存');
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'api-key-create') {
      const name = String(elements.membershipPlans?.querySelector('#membershipApiKeyName')?.value || '').trim() || '默认密钥';
      const scopes = String(elements.membershipPlans?.querySelector('#membershipApiKeyScopes')?.value || '')
        .split(',')
        .map((item) => String(item || '').trim().toLowerCase())
        .filter(Boolean);
      const payload = await requestJson('/api/membership/center/api-keys', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, scopes })
      });
      const token = String(payload?.token || '');
      if (token) {
        showToast(`API Key 已创建：${token.slice(0, 14)}...`);
      } else {
        showToast('API Key 已创建');
      }
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'api-key-revoke') {
      const apiKeyId = Number(triggerEl?.getAttribute('data-api-key-id') || 0);
      if (!apiKeyId) return;
      await requestJson(`/api/membership/center/api-keys/${apiKeyId}/revoke`, { method: 'POST' });
      showToast('API Key 已吊销');
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'coupon-redeem') {
      const code = String(elements.membershipPlans?.querySelector('#membershipCouponCode')?.value || '').trim();
      if (!code) {
        showToast('请输入优惠码');
        return;
      }
      const result = await requestJson('/api/membership/center/coupons/redeem', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code })
      });
      showToast(`兑换成功：${Number(result?.discountPercent || 0)}%`);
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'ticket-create') {
      const note = String(elements.membershipPlans?.querySelector('#membershipTicketNote')?.value || '').trim();
      const attachment = String(elements.membershipPlans?.querySelector('#membershipTicketAttachment')?.value || '').trim();
      if (!note) {
        showToast('请填写工单内容');
        return;
      }
      await requestJson('/api/membership/center/tickets', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ note, attachment })
      });
      showToast('工单已提交');
      await loadMembershipCenterData(true);
      return;
    }

    if (action === 'ticket-status-save') {
      const row = triggerEl?.closest('tr[data-ticket-id]');
      const ticketId = Number(row?.getAttribute('data-ticket-id') || 0);
      const status = String(row?.querySelector('[data-ticket-status]')?.value || '').toLowerCase();
      if (!ticketId || !status) return;
      await requestJson(`/api/membership/center/tickets/${ticketId}/status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status })
      });
      showToast('工单状态已更新');
      await loadMembershipCenterData(true);
    }
  } catch (error) {
    showToast(error?.message || '操作失败');
  }
}

async function loadAuthAndPlans() {
  ensureAuthStateShape();
  try {
    const [me, plansPayload] = await Promise.all([
      requestJson('/api/auth/me'),
      requestJson('/api/plans')
    ]);
    state.auth.plans = normalizePlans(plansPayload?.plans || []);
    applyMePayload(me);
  } catch {
    state.auth.initialized = true;
    state.auth.authenticated = false;
    state.auth.user = null;
    state.auth.membership = {
      ...DEFAULT_MEMBERSHIP,
      features: { ...DEFAULT_FEATURES }
    };
    state.auth.plans = normalizePlans(PLAN_FALLBACK);
  }

  updateAvatarUi();
  updateFeatureUi();
}

function bindEvents() {
  ensureUserMenuPortal();

  elements.userAvatarBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleUserMenu();
  });

  elements.userLoginBtn?.addEventListener('click', () => {
    closeUserMenu();
    openAuthOverlay('login');
  });

  elements.userLogoutBtn?.addEventListener('click', async () => {
    await logout();
    showToast('已退出登录');
  });

  elements.userMembershipBtn?.addEventListener('click', () => {
    closeUserMenu();
    openMembershipCenter();
  });

  elements.userAuthClose?.addEventListener('click', closeAuthOverlay);
  elements.userAuthOverlay?.addEventListener('click', (e) => {
    if (e.target === elements.userAuthOverlay) closeAuthOverlay();
  });
  elements.userAuthSubmitBtn?.addEventListener('click', submitAuth);
  elements.userAuthToggleModeBtn?.addEventListener('click', () => {
    openAuthOverlay(authMode === 'register' ? 'login' : 'register');
  });

  elements.membershipClose?.addEventListener('click', closeMembershipCenter);
  elements.membershipOverlay?.addEventListener('click', (e) => {
    if (e.target === elements.membershipOverlay) closeMembershipCenter();
  });

  elements.membershipPlans?.addEventListener('click', async (e) => {
    const tabBtn = e.target instanceof Element ? e.target.closest('[data-center-tab]') : null;
    if (tabBtn) {
      const tab = String(tabBtn.getAttribute('data-center-tab') || 'plans');
      setCenterTab(tab);
      return;
    }

    const planBtn = e.target instanceof Element ? e.target.closest('[data-plan-action]') : null;
    if (planBtn) {
      const code = planBtn.getAttribute('data-plan-action');
      if (!code) return;
      await switchPlan(code);
      return;
    }

    const actionBtn = e.target instanceof Element ? e.target.closest('[data-center-action]') : null;
    if (actionBtn) {
      const action = String(actionBtn.getAttribute('data-center-action') || '');
      if (!action) return;
      await performCenterAction(action, actionBtn);
    }
  });

  document.addEventListener('click', (e) => {
    const root = elements.userAvatarWrap;
    const menu = elements.userMenu;
    if (!root || !menu) return;
    if (e.target instanceof Node && root.contains(e.target)) return;
    if (e.target instanceof Node && menu.contains(e.target)) return;
    closeUserMenu();
  });

  window.addEventListener('resize', () => {
    if (!isUserMenuOpen()) return;
    positionUserMenu();
  });

  window.addEventListener('scroll', () => {
    if (!isUserMenuOpen()) return;
    positionUserMenu();
  }, true);
}

const PLAN_FALLBACK = [
  {
    code: 'free',
    name: 'Free',
    priceCents: 0,
    periodDays: 30,
    features: { ...DEFAULT_FEATURES }
  },
  {
    code: 'pro',
    name: 'Pro',
    priceCents: 1990,
    periodDays: 30,
    features: {
      ...DEFAULT_FEATURES,
      canAccessAi: true,
      canAccessMusic: true,
      canImportMusic: true,
      canManageAiModels: true,
      maxBooks: 500
    }
  },
  {
    code: 'max',
    name: 'Max',
    priceCents: 4990,
    periodDays: 30,
    features: {
      ...DEFAULT_FEATURES,
      canAccessAi: true,
      canAccessMusic: true,
      canImportMusic: true,
      canManageAiModels: true,
      maxBooks: null
    }
  }
];

export async function initMembershipModule(context = {}) {
  bindDeps(context);
  if (!state || !elements) {
    throw new Error('Membership module requires state and elements');
  }
  if (membershipModuleInitialized) return;
  membershipModuleInitialized = true;
  bindEvents();
  await loadAuthAndPlans();
}

export function isMembershipModuleInitialized() {
  return membershipModuleInitialized;
}

export function getMembershipPlanCode(appState) {
  return String(appState?.auth?.membership?.planCode || 'free').toLowerCase();
}

export function isCurrentUserAdmin(appState) {
  const role = String(appState?.auth?.user?.role || 'user').toLowerCase();
  return (ROLE_LEVEL[role] || 0) >= ROLE_LEVEL.support;
}

export function getMembershipFeatures(appState) {
  return {
    ...DEFAULT_FEATURES,
    ...(appState?.auth?.membership?.features || {})
  };
}

export function canAccessFeature(appState, featureName) {
  const features = getMembershipFeatures(appState);
  return !!features?.[featureName];
}

export function getBookLimit(appState) {
  const features = getMembershipFeatures(appState);
  const limit = features?.maxBooks;
  return Number.isFinite(limit) ? Number(limit) : null;
}
